import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowRight, Search, UserRoundPlus, UsersRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
	AppUser,
	fetchPatientCounts,
	fetchPatientDetail,
	fetchPatients,
	isManagementEnded,
	isUnderManagement,
	PatientStatus,
	RiskLevel,
	type PatientListItem,
} from '@/api/adapters/patients';
import { ApiError } from '@/api/error';
import { toast } from '@/app/store/toast.store';
import { useAuthCanRegisterPatient } from '@/app/store/auth.store';
import {
	currentProWeekIndex,
	formatDate,
	maskNameKeepLast,
	patientStatusLabelKey,
	treatmentWeeks,
} from '@/pages/medical/screens/home/utils';

import {
	downloadPatientProCsv,
	fetchInstallSmsStatus,
} from '@/api/adapters/patients';
import type { PatientRegistrationFormValues } from '@/features/patients/schemas/patient-registration.schema';
import {
	usePatientDetail,
	useRegisterPatient,
	useResendInstallSms,
} from '@/shared/hooks/use-patient-actions';
import { useProTreatmentBar } from '@/shared/hooks/use-pro-data';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useInfiniteScrollSentinel } from '@/shared/hooks/use-infinite-scroll-sentinel';
import { useSendSms } from '@/shared/hooks/use-patients-list';
import { BiometricTab } from './BiometricTab';
import { InstallSmsFailModal } from './InstallSmsFailModal';
import { InterventionPanel } from './InterventionPanel';
import { InterventionTab } from './InterventionTab';
import { PatientDetailHeader } from './PatientDetailHeader';
import { PatientFormModal } from './PatientFormModal';
import { ProCalendar } from './ProCalendar';
import { ProToolbar } from './ProToolbar';
import { ScheduleAddModal, type ScheduleEditTarget } from './ScheduleAddModal';
import { RiskPill } from '@/components/ui/RiskPill';
import { useInterventionPanelStore } from '@/app/store/intervention-panel.store';

// Helpers for card display
// Card 이름줄 `(성별약자+나이)` — age in full years from the (guardian-supplied) birth date.
// Returns null when the birth date is absent (patient mode before PASS delivers it).
const ageFromBirth = (iso: string): number | null => {
	if (!iso) return null;
	const b = new Date(iso);
	if (Number.isNaN(b.getTime())) return null;
	const now = new Date();
	let age = now.getFullYear() - b.getFullYear();
	const m = now.getMonth() - b.getMonth();
	if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
	return age >= 0 ? age : null;
};

export function MedicalPatientsPage() {
	const { t } = useTranslation(['medical', 'common']);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const canRegister = useAuthCanRegisterPatient();
	const selectedUid = searchParams.get('selected') || null;
	// Derive the active tab from the URL (single source of truth) so switching cards — which
	// rewrites ?tab=pro — always keeps the panel and the URL in sync (was a stale local state).
	const activeTab = searchParams.get('tab') || 'pro';
	const [filterTab, setFilterTab] = useState<string>('all');
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search);

	const registerMutation = useRegisterPatient();
	const resendInstallSmsMutation = useResendInstallSms();
	// Set once a just-registered patient's install-guide SMS is known to have failed; null hides the modal.
	const [installSmsFail, setInstallSmsFail] = useState<{
		uid: string;
		canResend: boolean;
	} | null>(null);

	const {
		data: pagesData,
		isLoading,
		isSuccess,
		isError: patientsLoadError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ['patients', 'list', debouncedSearch, filterTab],
		// PRD §3.1: on load, sort by R-value descending (주의→일반). BE PLAIN_SORT_KEYS accepts riskScore.
		queryFn: ({ pageParam }) =>
			fetchPatients({
				page: pageParam,
				size: 50,
				search: debouncedSearch || undefined,
				sortKey: 'riskScore',
				sortDir: 'desc',
				// 주의/지연/종료 tabs → server-side filters (SHCT-199/200; BE-1 flags). 전체 = unfiltered.
				...(filterTab === 'danger'
					? { dangerOnly: true }
					: filterTab === 'delayed'
						? { delayedOnly: true }
						: filterTab === 'closed'
							? { status: 'closed' }
							: {}),
			}),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.data.length >= 50 ? lastPage.page + 1 : undefined,
		staleTime: 60_000,
	});

	const patientsData = pagesData?.pages.flatMap((p) => p.data) ?? [];

	// PRD §3.1: when entering from Home with ?selected=<uid>, auto-scroll that card into view.
	const activeCardRef = useRef<HTMLButtonElement | null>(null);

	// A ?selected=<uid> patient may sit on a not-yet-loaded page (list is paginated, 50/page), so
	// loaded-list membership is NOT a valid authorization test — it false-denies page-2+ patients
	// (SHCT-201). Resolve authoritatively via the RBAC-gated detail endpoint when the patient is not
	// already loaded; the BE returns PATIENT_NOT_FOUND iff the patient is unknown/out-of-scope.
	const listMatch = useMemo(
		() => patientsData.find((p) => p.uid === selectedUid) ?? null,
		[patientsData, selectedUid],
	);

	// Runs for EVERY selected patient, not only the ones missing from the loaded list. Two jobs now share it:
	// the authorization resolve above, and the header's 전화번호 (SHCT-893 / PRD 04 v2.30) — the list response
	// masks the number (PRD 03), so the raw one the header is specified to show can only come from here.
	const {
		data: fetchedPatient,
		isError: detailIsError,
		error: detailError,
	} = useQuery({
		queryKey: ['patient', 'detail', selectedUid],
		queryFn: () => fetchPatientDetail(selectedUid as string),
		enabled: !!selectedUid && isSuccess,
		retry: false,
		staleTime: 60_000,
	});

	const selectedPatient = listMatch ?? fetchedPatient ?? null;
	// PRD 04 v2.30 — the header prints the patient's own mobile unmasked; see the header call below.
	const { data: unmaskedPatient } = usePatientDetail(selectedUid ?? undefined, true);

	// FE-9 (PD-07/08): block only on a genuine authorization/scope failure (PATIENT_NOT_FOUND from the
	// detail endpoint) — redirect to the Patient List + show the authorization notice. A transient
	// system error surfaces the load notice instead and keeps the selection.
	// Scoped to `!listMatch` on purpose. The query above now also runs for patients already in the list, and
	// there the detail call is a supplement (전화번호), not the authorization test — a transient failure must
	// not toast or deselect a patient the list already proved this staff can see.
	useEffect(() => {
		if (!selectedUid || !detailIsError || listMatch) return;
		if (detailError instanceof ApiError && detailError.code === 'PATIENT_NOT_FOUND') {
			toast.error(t('medical:patients.errors.accessDenied'));
			const next = new URLSearchParams(searchParams);
			next.delete('selected');
			setSearchParams(next, { replace: true });
		} else {
			toast.error(t('medical:patients.errors.loadPatient'));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUid, detailIsError, detailError, listMatch]);

	// PRD §3.1: scroll the selected card into view once the list is loaded (deep-link entry).
	useEffect(() => {
		if (isSuccess && selectedPatient && activeCardRef.current) {
			activeCardRef.current.scrollIntoView({ block: 'nearest' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess, selectedUid, selectedPatient]);

	// Shares the PRO heatmap query — surfaces the PRO load error (treatment bar itself removed to match prototype).
	const { isError: proLoadError } = useProTreatmentBar(selectedPatient?.uid);
	// FE-7: PRO reference date is Today, not the discharge date.
	const todayIso = new Date().toISOString().slice(0, 10);

	// FE-20 (PD-08): system load-error notices with the spec strings.
	useEffect(() => {
		if (patientsLoadError) toast.error(t('medical:patients.errors.loadPatient'));
	}, [patientsLoadError, t]);
	useEffect(() => {
		if (proLoadError) toast.error(t('medical:patients.errors.loadPro'));
	}, [proLoadError, t]);

	// Sync intervention panel when a different patient is selected while panel is open
	useEffect(() => {
		const panelStore = useInterventionPanelStore.getState();
		if (
			panelStore.open &&
			selectedPatient &&
			selectedPatient.uid !== panelStore.patient?.uid
		) {
			panelStore.openPanel(selectedPatient);
		}
	}, [selectedPatient]);

	// SHCT-199/200: per-tab totals computed server-side over the FULL care-team set (was a client-side count
	// that only saw the loaded infinite-scroll pages). danger = the 고위험/산출 불가 risk band. Refetched after
	// register/close/revert via the ['patients'] invalidation in use-patient-actions.
	const { data: counts } = useQuery({
		queryKey: ['patients', 'counts'],
		queryFn: fetchPatientCounts,
		staleTime: 60_000,
	});

	// Infinite scroll sentinel
	const sentinelRef = useInfiniteScrollSentinel({
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	});

	// The active tab is now applied server-side in the list query (SHCT-199/200), so the loaded pages already
	// hold only the tab's patients — no client-side re-filter, and the list is complete, not page-windowed.
	const filteredPatients = patientsData;

	// The synthesized head entry exists ONLY for the SHCT-201 deep-link case: a ?selected=<uid> patient that
	// belongs in this list but sits on a page the infinite scroll has not fetched yet (50/page). It must NOT
	// be used to pin a patient the current view legitimately excludes — a 주의/지연/종료 tab or a search term
	// filters server-side, so "absent from the loaded pages" there means "filtered out", not "not loaded yet".
	// Pinning it anyway put an ACTIVE patient at the top of 종료, made the rendered rows exceed the tab badge
	// (counts come from /patients/counts, which never sees the synthesized row), and survived a non-matching
	// search. Gate on the unfiltered view + a page still outstanding; the detail panel keeps rendering the
	// selected patient either way, so switching tabs no longer drops the selection — only its list card.
	const isUnfilteredView = filterTab === 'all' && !debouncedSearch;
	const displayPatients = useMemo(
		() =>
			selectedPatient && !listMatch && isUnfilteredView && hasNextPage
				? [selectedPatient, ...filteredPatients]
				: filteredPatients,
		[
			selectedPatient,
			listMatch,
			isUnfilteredView,
			hasNextPage,
			filteredPatients,
		],
	);

	const [showRegModal, setShowRegModal] = useState(false);
	const [showSchModal, setShowSchModal] = useState(false);
	// FE-13: when set, the schedule modal opens in edit mode for this future schedule.
	const [schEditTarget, setSchEditTarget] = useState<ScheduleEditTarget | null>(
		null,
	);

	// [전체 환자 보기] from Patient Management redirects to Home and opens the modal there
	// (SHCT-462 / PRD 02 §3.5.1 · §3.5.10) — Home owns the full-patient-list modal.
	const handleViewAll = useCallback(() => {
		navigate('/home?openViewAll=1');
	}, [navigate]);

	// Auto-open registration modal when arriving with ?openReg=1
	useEffect(() => {
		if (searchParams.get('openReg') === '1') {
			setShowRegModal(true);
			const next = new URLSearchParams(searchParams);
			next.delete('openReg');
			setSearchParams(next, { replace: true });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleRegisterClick = useCallback(() => setShowRegModal(true), []);
	const handleRegClose = useCallback(() => setShowRegModal(false), []);

	const handleRegSubmit = (values: PatientRegistrationFormValues) => {
		registerMutation.mutate(values, {
			onSuccess: (patient) => {
				setShowRegModal(false);
				// The patient is saved; the install guide goes out afterwards, so its outcome is only
				// readable once the server has actually tried to send it. Ask, and raise the modal only
				// when that attempt has FAILED — a PENDING attempt is still in flight and says nothing yet.
				void fetchInstallSmsStatus(patient.uid)
					.then((s) => {
						if (s.status === 'FAILED') {
							setInstallSmsFail({ uid: patient.uid, canResend: s.canResend });
						}
					})
					.catch(() => {
						// Registration succeeded; failing to read the SMS outcome must not surface as a
						// registration error. The clinician can resend from the patient's detail screen.
					});
			},
		});
	};

	const handleInstallSmsResend = () => {
		if (!installSmsFail) return;
		resendInstallSmsMutation.mutate(installSmsFail.uid, {
			onSuccess: ({ success }) => {
				// Keep the modal open on a failed resend so the clinician can try again; the hook has
				// already reported a successful send.
				if (success) setInstallSmsFail(null);
			},
		});
	};

	const handleCardClick = useCallback(
		(uid: string) => {
			navigate(`/patients?selected=${uid}&tab=pro`, { replace: true });
		},
		[navigate],
	);

	const remindMutation = useSendSms();

	const handleRemind = useCallback(
		(e: React.MouseEvent, p: PatientListItem) => {
			e.stopPropagation();
			remindMutation.mutate({ uid: p.uid, name: p.name, phone: p.phone });
		},
		[remindMutation],
	);

	const c = counts ?? { all: 0, danger: 0, delayed: 0, closed: 0 };
	// 전체 카운트 = 로드된 모든 환자(관리종료·탈퇴 포함) — 결정 B.
	const allCount = c.all ?? 0;

	return (
		<div className='pm-layout pm-layout--v31'>
			<aside className='pm-left'>
				<div className='pm-left__head'>
					<h3 className='pm-left__title'>{t('medical:patients.list.title')}</h3>
					<div className='pm-left__btn-row'>
						<button
							type='button'
							className='btn btn--secondary'
							onClick={handleViewAll}
						>
							<UsersRound size={16} />
							<span>{t('medical:patients.list.actions.viewAll')}</span>
						</button>
						<button
							type='button'
							className='btn btn--primary'
							onClick={handleRegisterClick}
							disabled={!canRegister}
						>
							<UserRoundPlus size={16} />
							<span>{t('medical:patients.list.actions.register')}</span>
						</button>
					</div>
				</div>
				<div className='pm-left__search'>
					<div className='filter-bar__search'>
						<Search />
						<input
							type='text'
							placeholder={t('medical:patients.list.search.placeholder')}
							value={search}
							maxLength={50}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>
				<div className='pm-filter-tabs'>
					<button
						type='button'
						className={`pm-filter-tab${filterTab === 'all' ? ' pm-filter-tab--active' : ''}`}
						onClick={() => setFilterTab('all')}
					>
						{t('medical:patients.list.filters.all')} <span>{allCount}</span>
					</button>
					<button
						type='button'
						className={`pm-filter-tab pm-filter-tab--danger${filterTab === 'danger' ? ' pm-filter-tab--active' : ''}`}
						onClick={() => setFilterTab('danger')}
					>
						{t('medical:patients.list.filters.danger')} <span>{c.danger}</span>
					</button>
					<button
						type='button'
						className={`pm-filter-tab${filterTab === 'delayed' ? ' pm-filter-tab--active' : ''}`}
						onClick={() => setFilterTab('delayed')}
					>
						{t('medical:patients.list.filters.delayed')}{' '}
						<span>{c.delayed}</span>
					</button>
					<button
						type='button'
						className={`pm-filter-tab${filterTab === 'closed' ? ' pm-filter-tab--active' : ''}`}
						onClick={() => setFilterTab('closed')}
					>
						{t('medical:patients.list.filters.closed')} <span>{c.closed}</span>
					</button>
				</div>
				<div className='pm-left__list'>
					{isLoading ? (
						Array.from({ length: 5 }, (_, i) => (
							<div key={i} className='patient-card animate-pulse'>
								<div className='patient-card__head'>
									<div className='h-4 w-20 bg-gray-200 rounded mb-2' />
									<div className='h-3 w-32 bg-gray-200 rounded' />
								</div>
								<div className='patient-card__body'>
									<div className='h-3 w-40 bg-gray-200 rounded' />
								</div>
							</div>
						))
					) : displayPatients.length === 0 ? (
						<div className='pm-left__empty'>
							<p>
								{debouncedSearch || filterTab !== 'all'
									? t('medical:patients.empty.noResult')
									: t('medical:patients.empty.noPatients')}
							</p>
							{/* PRD §3.1/§5 빈 상태(신규): 담당 환자 0명일 때 신규 등록 안내 CTA */}
							{!debouncedSearch && filterTab === 'all' && canRegister && (
								<button
									type='button'
									className='btn btn--primary mt-3'
									onClick={handleRegisterClick}
								>
									<UserRoundPlus size={16} />
									<span>{t('medical:patients.list.actions.register')}</span>
								</button>
							)}
						</div>
					) : (
						displayPatients.map((p) => {
						const isActive = selectedUid === p.uid;
						// Monday-anchored PRO week — the same helper (and so the same number) as the detail
						// header, frozen at 종료일 once management ended. The card used to show a live
						// "N주 경과" for an ended patient, which both kept climbing and made a closed card
						// read like an active one under the 전체 tab.
						const hasEnded =
							p.status === PatientStatus.CLOSED ||
							p.status === PatientStatus.WITHDRAWN;
						const weeks = treatmentWeeks(
							p.dischargeDate,
							hasEnded,
							p.closeDate,
						);
						// [MED.018] — 상태 표시는 `closeReason` 축이다. 값이 있으면 `관리종료 · 사유`,
						// 없으면 관리중(배지 없음). 라벨과 노출 조건이 같은 축을 봐야 둘이 갈리지 않는다.
						const hasCloseReason = !!p.closedReason;
						const closedLabel = t(
							patientStatusLabelKey(p.status, p.closedReason),
						);
						return (
							<button
								key={p.uid}
								ref={isActive ? activeCardRef : undefined}
								type='button'
								className={`patient-card${isActive ? ' patient-card--active' : ''}`}
								data-risk={
									isManagementEnded(p.status) ? 'closed' : undefined
								}
								onClick={() => handleCardClick(p.uid)}
							>
								<div className='patient-card__head'>
									<span className='patient-card__name'>
										{maskNameKeepLast(p.name)}
									</span>
									<div className='patient-card__meta'>
										<span className='patient-card__meta-item'>
											{p.gender}
											{ageFromBirth(p.birthDate) ?? ''}
										</span>
										<span className='patient-card__sep' />
										<span className='patient-card__meta-item'>
											{formatDate(p.birthDate)}
										</span>
										{p.appUser === AppUser.GUARDIAN && p.guardian && (
											<>
												<span className='patient-card__sep' />
												<span
													className='patient-card__guardian-dot'
													title={t('medical:patients.card.guardianApp', {
														relation: t(
															'medical:patients.register.staff.relationship.' +
																p.guardian.relationship,
														),
													})}
												>
													{t('medical:patients.card.guardianShort')}
												</span>
											</>
										)}
									</div>
									{p.status === PatientStatus.CLOSED ||
									p.status === PatientStatus.WITHDRAWN ? (
										// 종료 상태(관리종료 CLOSED · 탈퇴 WITHDRAWN)는 R칩을 회색 R—로 통일.
										// WITHDRAWN은 CLOSED 하위사유가 아니라 환자앱이 쓰는 별도 top-level status
										// (BE MedicalPatientService DB_STATUSES) → CLOSED-only 가드로는 빠져나가
										// 점수 있는 탈퇴 환자가 색상 R칩으로 렌더돼 CLOSED의 회색 R—와 섞여 보였음. 두 종료 상태를 함께 게이트.
										<span className='patient-card__r-pill patient-card__r-pill--empty'>
											R —
										</span>
									) : p.status === PatientStatus.ACTIVE &&
									  p.riskLevel === RiskLevel.UNKNOWN ? (
										// 산출 불가 = ACTIVE + riskLevel 'UNKNOWN' (BE RiskComputation Rule B, PRD 11 §7.2).
										// 빨강 산출 불가는 활성 환자 전용(위 종료 가드에서 CLOSED/WITHDRAWN은 이미 회색 R—로 처리).
										// 미문진 신규(untouched) 환자는 riskLevel null → 산출 불가 아님(회색 R—), 강제 노출 금지.
										<span
											className='patient-card__r-pill patient-card__r-pill--unknown'
											title={t('medical:patients.card.riskUnknownTooltip')}
										>
											{t('medical:patients.card.riskUnknown')}
										</span>
									) : p.riskScore != null ? (
										// SHCT-828 (2차) — 카드도 헤더와 같은 한 알약 `R 1.1 · 저위험`.
										// 카드는 점수만(`R 1.1`) 찍고 위험도 라벨이 없어, 같은 화면 오른쪽
										// 헤더가 `R 1.1 · 저위험`을 보여주는 것과 갈렸다(고객 재보고 2026-09-04).
										// 라벨·색 매핑은 RiskPill 한 곳에만 두어 다시 갈라지지 않게 한다.
										// 종료(관리종료·탈퇴) 게이트는 위에서 이미 처리되므로 여기 오지 않는다.
										<RiskPill
											variant='card'
											riskScore={p.riskScore}
											riskLevel={p.riskLevel}
											t={t}
										/>
									) : (
										<span className='patient-card__r-pill patient-card__r-pill--empty'>
											R —
										</span>
									)}
								</div>
								<div className='patient-card__body'>
									<div className='patient-card__protocol'>
										<span className='patient-card__discharge'>
											{t('medical:patients.card.dischargeText', {
												date: formatDate(p.dischargeDate),
											})}
										</span>
										{/* Ended patient → the header's "N주 후 종료" wording, not "N주 경과", so the card
										    says the same thing the detail panel does. No 종료일 → drop the number. */}
										<span className='patient-card__weeks-chip'>
											{hasEnded
												? weeks == null
													? t('medical:patients.header.treatment.ended')
													: t('medical:patients.header.treatment.weeksToEnd', {
															weeks,
														})
												: t('medical:patients.card.weeksElapsed', {
														weeks: weeks ?? 0,
													})}
										</span>
										{/* PRD 03 §2.3/§3.1: 이번 주 단계 3(G3) 응답이 1건 이상이면 "G3+" 등급 칩 노출
										    (프로토타입: 등급 칩 + '+' 배지). 0건·관리종료 미노출. 노출 조건값은 BE 제공 stage3Count;
										    미제공 시 undefined → 칩 미노출(가짜 데이터 방지). 정확한 건수는 hover 툴팁으로 표시. */}
										{isUnderManagement(p.status) &&
											p.stage3Count != null &&
											p.stage3Count > 0 && (
												<span
													className='patient-card__legend'
													title={t('medical:patients.card.stage3', {
														count: p.stage3Count,
													})}
												>
													<span className='patient-card__g-chip patient-card__g-chip--g3'>
														{t('medical:patients.pro.gradeShort.g3')}
													</span>
													<span
														className='patient-card__g-plus'
														aria-hidden='true'
													/>
												</span>
											)}
									</div>
									{/* SHCT-672: the 관리종료 badge belongs to the PATIENT, not to the tab being viewed —
									    it must show wherever the card is listed. It used to be gated on
									    `filterTab === 'closed'`, following PRD 03 §2.3/§3.1 ("관리종료(필터=종료)" /
									    "IF 종료 필터 ON"), so a closed patient under 전체 was indistinguishable from an
									    active one. Clarification MED.008 ("환자카드에는 현재 프로토타입에 정의된 내용으로
									    진행") outranks the feature PRD for the patient card, and the prototype
									    (patients.html) renders this row on `status === 'closed'` alone, no tab test.
									    Missing 사유 falls back to a bare 관리종료, the prototype's reason-less branch —
									    a closed patient must never render as if still under management.
									    [MED.018] (PRD 03 §4.3) — 노출 조건도 `closeReason` 축으로 옮긴다. 종전에는
									    `status`로 노출을 정하고 라벨만 사유를 읽어, 자발 탈퇴 환자가 `탈퇴 · 탈퇴`로
									    찍혔고 정상만기 후 탈퇴한 환자는 임상 이력이 '탈퇴'로 덮였다. 두 축을 하나로
									    합치면 배지 유무와 문구가 같은 근거를 쓴다. */}
									{hasCloseReason && (
										<div className='patient-card__closed-row'>
											<span
												className='chip-pill chip-pill--gray'
												title={closedLabel}
											>
												{closedLabel}
											</span>
										</div>
									)}
									{/* Reminders are for patients still under care only. The stored "behind on questionnaires"
									    flag is not cleared when management ends, so a patient who has left can still arrive
									    carrying it — gating on ACTIVE is what stops the button appearing for someone who can
									    no longer sign in to act on it. */}
									{p.delayed && isUnderManagement(p.status) && (
										<button
											type='button'
											className='patient-card__remind'
											onClick={(e) => handleRemind(e, p)}
										>
											<span className='patient-card__remind-text'>
												{t('medical:patients.card.proDelayed')}
											</span>
											<span className='patient-card__remind-action'>
												{t('medical:patients.card.sendReminder')}
												<ArrowRight size={16} />
											</span>
										</button>
									)}
								</div>
							</button>
						);
				}))}
					{hasNextPage && (
						<div
							ref={sentinelRef}
							className='py-4 text-center'
						>
							{isFetchingNextPage && (
								<div className='h-4 w-24 bg-gray-200 animate-pulse rounded mx-auto' />
							)}
						</div>
					)}
				</div>
			</aside>
			<section className='pm-right'>
				{selectedPatient ? (
					<>
						<PatientDetailHeader
							patient={selectedPatient}
							// PRD 04 v2.30 — the header's 전화번호 is the RAW number: 전화 중재 (§3.4) is on this
							// same screen and a masked number cannot be dialled. medical-api masks name and phone
							// unless the read asks for them in the clear, so the raw value needs its OWN read —
							// the detail query above is the masked one, and `selectedPatient` falls back to it,
							// so unmasking it would put an unmasked record behind every consumer on this page.
							// This hook keys masked and unmasked under different cache entries, and the edit
							// modal already warms the same one.
							phone={unmaskedPatient?.phone}
							onScheduleAdd={() => {
								setSchEditTarget(null);
								setShowSchModal(true);
							}}
						/>
						<nav className='ph-tabs'>
							{[
								{ key: 'pro', label: t('medical:patients.tabs.pro') },
								{
									key: 'biometric',
									label: t('medical:patients.tabs.biometric'),
								},
								{
									key: 'intervention',
									label: t('medical:patients.tabs.intervention'),
								},
							].map((tab) => (
								<button
									key={tab.key}
									type='button'
									className={`ph-tabs__item${activeTab === tab.key ? ' ph-tabs__item--active' : ''}`}
									onClick={() =>
										navigate(
											`/patients?selected=${selectedUid}&tab=${tab.key}`,
											{ replace: true },
										)
									}
								>
									{tab.label}
								</button>
							))}
						</nav>
						{/*
							SHCT-718 — keyed by patient + tab so switching patients REMOUNTS this
							subtree instead of reconciling one patient's detail into another's.
							Reconciling here is what let a DOM node be reused across two structurally
							different renders, which is how the removeChild NotFoundError arose. The
							trees are also cheap to rebuild (data comes from cached queries), so the
							remount costs nothing a user can perceive.
						*/}
						<div
							key={`${selectedUid}-${activeTab}`}
							className='ph-content flex-1 overflow-auto p-6'
						>
							{activeTab === 'pro' && selectedPatient && (
								<>
									{/* Treatment period lives in the PatientHeader (prototype); the PRO toolbar
									    carries 기준일(baseline) + CSV. PRD §2.2/§3.2 treatment bar removed to match prototype. */}
									<ProToolbar
										referenceDate={todayIso}
										onExportCsv={async () => {
											const areaLabels = t(
												'medical:patients.pro.csv.areaLabels',
												{ returnObjects: true },
											) as unknown as string[];
											try {
												await downloadPatientProCsv(selectedPatient, areaLabels);
											} catch (err) {
												// PD-08: CSV download authorization validated at execution.
												// BE returns PATIENT_NOT_FOUND (anti-enumeration) for out-of-scope access.
												const code = err instanceof ApiError ? err.code : "";
												const denied =
													code === "PATIENT_NOT_FOUND" ||
													code.includes("FORBIDDEN") ||
													code.includes("PERMISSION");
												toast.error(
													denied
														? t("medical:patients.errors.csvAccessDenied")
														: t("common:error.generic"),
												);
											}
										}}
									/>
									<ProCalendar
										patientUid={selectedPatient.uid}
										// Anchor to the Monday of the discharge week (same as the column layout),
										// not raw weeksSince — otherwise the highlight lags a week (see currentProWeekIndex).
										currentWeek={currentProWeekIndex(
											selectedPatient.dischargeDate,
										)}
										startDate={selectedPatient.dischargeDate}
									/>
								</>
							)}
							{activeTab === 'biometric' && selectedPatient && (
								<BiometricTab patient={selectedPatient} />
							)}
							{activeTab === 'intervention' && selectedPatient && (
								<InterventionTab
									patient={selectedPatient}
									onScheduleEdit={(target) => {
										setSchEditTarget(target);
										setShowSchModal(true);
									}}
								/>
							)}
						</div>
					</>
				) : (
					<div className='pm-right__empty'>
						<p>{t('medical:patients.empty.selectPatient')}</p>
					</div>
				)}
			</section>

			<PatientFormModal
				open={showRegModal}
				mode='create'
				onClose={handleRegClose}
				onSubmit={handleRegSubmit}
				isSubmitting={registerMutation.isPending}
			/>

			<InstallSmsFailModal
				open={installSmsFail !== null}
				canResend={installSmsFail?.canResend ?? true}
				isResending={resendInstallSmsMutation.isPending}
				onResend={handleInstallSmsResend}
				onClose={() => setInstallSmsFail(null)}
			/>

			<ScheduleAddModal
				open={showSchModal}
				patient={selectedPatient}
				patients={patientsData}
				editTarget={schEditTarget}
				/* SHCT-599 / PRD 04 §3.4 ② — [중재 시작]: close the modal and open the SAME 신규 중재
				   panel the header button opens, prefilled from this schedule (§3.4 ②-1). */
				onStartIntervention={(target) => {
					setShowSchModal(false);
					setSchEditTarget(null);
					if (!selectedPatient) return;
					useInterventionPanelStore
						.getState()
						.openPanel(selectedPatient, undefined, {
							scheduleId: target.id,
							scheduledAt: target.scheduledAt,
							scheduleType: target.type,
						});
				}}
				onClose={() => {
					setShowSchModal(false);
					setSchEditTarget(null);
				}}
			/>

			<InterventionPanel
				onScheduleAdd={() => {
					setSchEditTarget(null);
					setShowSchModal(true);
				}}
			/>
		</div>
	);
}
